import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHomeRoute } from '../utils/homeRoute';
import {
  Rocket,
  Lock,
  Users,
  Database,
  MessageSquare,
  Clock,
  Shield,
  Mail,
  BookOpen,
  Video,
  X
} from 'lucide-react';

const Welcome: React.FC = () => {
  const navigate = useNavigate();
  const [showSkipModal, setShowSkipModal] = useState(false);

  const handleBeginIntegration = () => {
    navigate('/billing/setup');
  };

  const handleSkipOnboarding = () => {
    setShowSkipModal(true);
  };

  const confirmSkip = () => {
    setShowSkipModal(false);
    navigate(getHomeRoute());
  };

  const steps = [
    {
      number: 1,
      icon: Lock,
      title: 'Grant Microsoft 365 Access',
      description: 'You\'ll be redirected to Microsoft to authorize Niwt to access your organization\'s data securely.',
    },
    {
      number: 2,
      icon: Shield,
      title: 'Verify Administrator Access',
      description: 'You\'ll need Global Administrator privileges for your Microsoft 365 organization to complete the setup.',
    },
    {
      number: 3,
      icon: Users,
      title: 'Select Users for Niwt Creation',
      description: 'Choose which employees in your organization will have their own AI-powered Niwt digital twin created.',
    },
    {
      number: 4,
      icon: Database,
      title: 'Data Ingestion & Processing',
      description: 'Niwt will securely ingest and analyze the selected data from your Microsoft 365 environment (typically takes 5-10 minutes).',
    },
    {
      number: 5,
      icon: Rocket,
      title: 'Niwt Creation',
      description: 'Your personalized Niwts will be generated based on the ingested data.',
    },
    {
      number: 6,
      icon: MessageSquare,
      title: 'Access Your Chat Dashboard',
      description: 'Start interacting with your Niwts through our intuitive chat interface.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <img
            src="/images/niwt-logo-horizontal.svg"
            alt="Niwt"
            className="h-10 w-auto"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-2xl mb-6 p-3">
            <img
              src="/images/niwt-logo.png"
              alt="Niwt"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 font-libre mb-3">
            Welcome to Niwt Platform!
          </h1>
          <p className="text-xl text-gray-600">
            Your AI-powered digital twins are almost ready
          </p>
        </div>

        {/* Time Estimate */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-5 h-5 text-niwt-primary" />
              <span className="text-sm font-medium">Estimated setup time: 10-15 minutes</span>
            </div>
          </div>
        </div>

        {/* Getting Started Steps */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 font-libre mb-6">
            Getting Started - Complete These Steps:
          </h2>
          <div className="space-y-4">
            {steps.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.number}
                  className="flex gap-4 p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
                >
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-semibold text-sm">
                      {step.number}
                    </div>
                  </div>
                  <div className="flex-shrink-0 mt-1">
                    <Icon className="w-5 h-5 text-niwt-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <button
            onClick={handleBeginIntegration}
            className="flex-1 bg-niwt-primary text-white font-semibold py-3 px-6 rounded-lg hover:bg-niwt-primary/90 transition-colors"
          >
            Continue Setup
          </button>
          <button
            onClick={handleSkipOnboarding}
            className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
          >
            Skip for Now
          </button>
        </div>

        {/* Help Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Need Help?
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4 text-niwt-primary" />
              <a href="#" className="text-niwt-primary hover:underline">
                Watch Tutorial Video
              </a>
              <span className="text-gray-500">(by Keelan)</span>
            </div>
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-niwt-primary" />
              <a href="/docs/onboarding" className="text-niwt-primary hover:underline">
                View Documentation
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-niwt-primary" />
              <a href="mailto:support@niwt.com" className="text-niwt-primary hover:underline">
                support@niwt.com
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Skip Warning Modal */}
      {showSkipModal && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40"
            onClick={() => setShowSkipModal(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-6 h-6 text-amber-600" />
                  <h3 className="text-xl font-semibold text-gray-900">
                    Integration Required
                  </h3>
                </div>
                <button
                  onClick={() => setShowSkipModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className="text-gray-700 mb-4">
                Niwt requires Microsoft 365 integration to create your digital twins.
              </p>

              <div className="mb-4">
                <p className="text-gray-700 font-medium mb-2">
                  Without completing the integration:
                </p>
                <ul className="list-disc ml-5 text-gray-600 text-sm space-y-1">
                  <li>No Niwts can be created for your organization</li>
                  <li>Chat features will operate in limited pass-through mode</li>
                  <li>You won't have access to personalized AI assistants</li>
                </ul>
              </div>

              <p className="text-sm text-gray-500 mb-6">
                You can return to complete integration at any time from Settings.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSkipModal(false)}
                  className="flex-1 bg-niwt-primary text-white font-semibold py-2 px-4 rounded-lg hover:bg-niwt-primary/90 transition-colors"
                >
                  Complete Integration Now
                </button>
                <button
                  onClick={confirmSkip}
                  className="px-4 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Continue Without
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Welcome;
